import React, { useState } from "react";
import { useAppStore } from "../../../store";
import { Button, notification, Input, Space } from "antd";
import { convertToInternationalFormat, getCouponErrorMessage } from "../../../utils";
import DataProvider from "../../../services";
import PhoneNumberInput from "../../../components/form/phone-number";
import { TagOutlined, CheckCircleFilled, ArrowLeftOutlined } from "@ant-design/icons";
import mtnLogo from "../../../assets/images/mtn.png";

export default function Pay(props: any) {
  const [api, contextHolder] = notification.useNotification();
  const { plan, state, user } = useAppStore();
  const [number, setNumber] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [couponInput, setCouponInput] = useState<string>("");
  const [appliedCoupon, setAppliedCoupon] = useState<string | null>(null);
  const [validatingCoupon, setValidatingCoupon] = useState<boolean>(false);

  const handleApplyCoupon = async () => {
    const code = couponInput?.trim().toUpperCase();
    if (!code) {
      api.warning({ message: "Coupon", description: "Enter a coupon code." });
      return;
    }
    setValidatingCoupon(true);
    try {
      const result = await DataProvider.validateCoupon(code);
      if (result.valid) {
        setAppliedCoupon(code);
        api.success({
          message: "Coupon applied",
          description: `"${code}" will be applied when you proceed.`,
        });
      } else {
        api.error({
          message: "Invalid coupon",
          description: result.message ?? "This coupon is not valid.",
        });
      }
    } catch {
      api.error({
        message: "Invalid coupon",
        description:
          "Could not validate coupon. Please check the code and try again.",
      });
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponInput("");
  };

  async function onRequestSub() {
    if (!number || number.length > 10 || number.length < 10) {
      return api["warning"]({
        message: "Warning",
        description: "Phone number is required.",
      });
    }

    if (!number.startsWith("0")) {
      return api["warning"]({
        message: "Warning",
        description: "Phone number must start with 0.",
      });
    }

    if (plan?.id == null) {
      return api["error"]({
        message: "Error",
        description: "Plan not selected. Please go back and choose a plan.",
      });
    }

    const msisdn = convertToInternationalFormat(number);
    const payload: { mcareId: number; msisdn: string; couponCode?: string } = {
      mcareId: plan.id,
      msisdn,
    };
    if (state === "SUB" && appliedCoupon) {
      payload.couponCode = appliedCoupon;
    }

    const payloadx = {
      subscriptionId: user?.subscription?.id,
      newMcareId: plan.id,
      msisdn,
    };

    try {
      setLoading(true);
      if (state === "SUB") {
        await DataProvider.requestSubscription(payload);
      } else if (state === "RENEW") {
        await DataProvider.renewSubscription(payload);
      } else if (state === "CHANGE") {
        await DataProvider.changeSubscription(payloadx);
      }

      setLoading(false);
      props.setScreen(3);
    } catch (error: any) {
      setLoading(false);
      const message = payload.couponCode
        ? getCouponErrorMessage(error)
        : error?.response?.data?.message ||
          (error.message === "ERR_NETWORK"
            ? "Couldn't verify, check your internet connection and try again."
            : error.message);
      return api["error"]({
        message: payload.couponCode ? "Coupon" : "Failed",
        description: message,
      });
    }
  }
  return (
    <div className="w-full">
      <Button
        onClick={() => props.setScreen(0)}
        icon={<ArrowLeftOutlined />}
        type="text"
        className="mb-3"
      >
        Back
      </Button>

      <div className="max-w-4xl mx-auto">
        <div className="mb-5 sm:mb-6">
          <h1 className="text-xl sm:text-2xl font-semibold text-neutral-900">
            {state === "RENEW"
              ? "Renew your subscription"
              : state === "CHANGE"
                ? "Confirm your plan change"
                : "Complete your subscription"}
          </h1>
          <p className="mt-1 text-sm text-neutral-600">
            Review your plan and pay to continue.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 sm:gap-6">
          {/* Order summary */}
          <div className="lg:col-span-2">
            <div className="rounded-2xl border border-neutral-200 bg-white shadow-sm overflow-hidden">
              <div className="px-5 py-4 bg-gradient-to-br from-[#1A94EC] to-[#0d7bc4] text-white">
                <p className="text-xs uppercase tracking-wide opacity-80">
                  Selected plan
                </p>
                <h2 className="text-xl font-bold mt-1">
                  {plan?.description ?? "—"}
                </h2>
                <p className="mt-2 text-2xl font-bold">
                  GH¢{plan?.price ?? 0}
                  <span className="text-sm font-normal opacity-80">
                    /month
                  </span>
                </p>
              </div>
              <div className="px-5 py-4 space-y-2.5">
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-600">Billing period</span>
                  <span className="font-medium text-neutral-900">
                    1 month
                  </span>
                </div>
                {appliedCoupon && (
                  <div className="flex justify-between text-sm">
                    <span className="text-neutral-600">Coupon</span>
                    <span className="font-medium text-green-600">
                      {appliedCoupon}
                    </span>
                  </div>
                )}
                <div className="pt-2.5 mt-1 border-t border-neutral-100 flex justify-between items-baseline">
                  <span className="text-sm font-medium text-neutral-900">
                    Total due today
                  </span>
                  <span className="text-lg font-bold text-neutral-900">
                    GH¢{plan?.price ?? 0}
                  </span>
                </div>
              </div>
              {plan?.offers && plan.offers.length > 0 && (
                <div className="px-5 pb-5 pt-1 border-t border-neutral-100">
                  <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2 mt-3">
                    What&apos;s included
                  </p>
                  <div className="space-y-3">
                    {plan.offers.map((offer, idx) => (
                      <div key={idx}>
                        <p className="text-sm font-semibold text-neutral-800 mb-1">
                          {offer.name}
                        </p>
                        <ul className="space-y-1">
                          {offer.list.map((item, idxx) => (
                            <li
                              key={idxx}
                              className="flex items-start gap-2 text-sm text-neutral-600"
                            >
                              <CheckCircleFilled className="text-[#1A94EC] mt-0.5 text-xs shrink-0" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Payment form */}
          <div className="lg:col-span-3">
            <div className="rounded-2xl border border-neutral-200 bg-white shadow-sm p-5 sm:p-6">
              <div className="flex items-center gap-3 mb-5 pb-4 border-b border-neutral-100">
                <img
                  src={mtnLogo}
                  alt="MTN"
                  className="h-8 w-8 object-contain shrink-0"
                />
                <div>
                  <h3 className="font-semibold text-neutral-900">
                    Mobile Money
                  </h3>
                  <p className="text-xs text-neutral-500">
                    Pay securely via MTN Mobile Money
                  </p>
                </div>
              </div>

              <PhoneNumberInput
                required
                title="Enter Phone Number"
                value={number}
                onChangeText={setNumber}
                placeholder="eg:0240005678"
              />

              {/* Apply coupon — only for new subscription */}
              {state === "SUB" && (
                <div className="mb-5 mt-4">
                  <label
                    htmlFor="coupon-input"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    <TagOutlined className="mr-1" aria-hidden />
                    Have a coupon?
                  </label>
                  {appliedCoupon ? (
                    <div className="flex items-center justify-between rounded-lg border border-green-200 bg-green-50 px-3 py-2">
                      <span className="text-sm font-medium text-green-800">
                        {appliedCoupon}
                      </span>
                      <Button
                        type="link"
                        size="small"
                        onClick={handleRemoveCoupon}
                        className="text-gray-600 p-0 h-auto"
                        aria-label="Remove coupon"
                      >
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <Space.Compact className="w-full">
                      <Input
                        id="coupon-input"
                        placeholder="Enter coupon code"
                        value={couponInput}
                        onChange={(e) => setCouponInput(e.target.value)}
                        onPressEnter={handleApplyCoupon}
                        className="rounded-l-lg rounded-r-none"
                        maxLength={64}
                        aria-label="Coupon code"
                      />
                      <Button
                        type="default"
                        onClick={handleApplyCoupon}
                        loading={validatingCoupon}
                        className="rounded-r-lg rounded-l-none shrink-0"
                        aria-label="Apply coupon"
                      >
                        Apply
                      </Button>
                    </Space.Compact>
                  )}
                </div>
              )}

              <p className="text-xs text-neutral-500 mb-5 mt-4">
                By clicking &quot;Proceed with payment&quot; you agree to the
                terms of service of the company.
              </p>

              <div className="flex flex-col-reverse sm:flex-row gap-3">
                <Button
                  onClick={() => props.setScreen(0)}
                  size="large"
                  className="w-full sm:w-auto"
                >
                  Exit
                </Button>
                <Button
                  onClick={onRequestSub}
                  type="primary"
                  size="large"
                  loading={loading}
                  disabled={loading}
                  style={{ backgroundColor: "#1A94EC" }}
                  className="w-full sm:flex-1"
                >
                  {loading ? "Processing..." : "Proceed with payment"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {contextHolder}
    </div>
  );
}
